export const EVALUATOR_FAMILY_VALUES = [
  "deterministic",
  "built_in_model",
  "custom_llm_judge",
  "external",
  "composite",
  "human_rubric",
] as const;
export type EvaluatorFamily = (typeof EVALUATOR_FAMILY_VALUES)[number];

export interface EvaluatorRegistryFilters {
  family?: EvaluatorFamily;
  lifecycle?: EvaluatorLifecycle;
}

export const BUILT_IN_EVALUATOR_ID_VALUES = [
  "topic-classification",
  "embedding",
  "token-count",
  "answer-relevance",
  "coherence",
  "conciseness",
  "context-relevance",
  "rag-faithfulness",
  "pii-detection",
  "sentiment-analysis",
  "ftl-prompt-safety",
  "ftl-response-faithfulness",
] as const;
export type BuiltInEvaluatorId = (typeof BUILT_IN_EVALUATOR_ID_VALUES)[number];

export const EVALUATOR_LIFECYCLE_VALUES = [
  "draft",
  "configured",
  "mapped",
  "tested",
  "approved",
  "shadow",
  "canary",
  "production",
  "degraded",
  "paused",
  "rolled_back",
  "retired",
] as const;
export type EvaluatorLifecycle = (typeof EVALUATOR_LIFECYCLE_VALUES)[number];

export const EVALUATOR_DETAIL_VIEW_VALUES = [
  "overview",
  "mapping",
  "tests",
  "rules",
  "versions",
  "deployments",
  "review-queue",
  "health",
  "evidence",
  "history",
] as const;
export type EvaluatorDetailView = (typeof EVALUATOR_DETAIL_VIEW_VALUES)[number];

export const EVALUATOR_SORT_VALUES = ["name", "lifecycle", "latency", "issues"] as const;
export type EvaluatorSort = (typeof EVALUATOR_SORT_VALUES)[number];
export const SORT_DIRECTION_VALUES = ["asc", "desc"] as const;
export type SortDirection = (typeof SORT_DIRECTION_VALUES)[number];

export type EvaluatorHealthStatus =
  | "healthy"
  | "degraded"
  | "unavailable"
  | "paused"
  | "cost_limited"
  | "data_restricted"
  | "mapping_broken";

export const EVALUATOR_HEALTH_STATUS_VALUES = [
  "healthy",
  "degraded",
  "unavailable",
  "paused",
  "cost_limited",
  "data_restricted",
  "mapping_broken",
] as const satisfies readonly EvaluatorHealthStatus[];

export interface EvaluatorRegistryListVersion {
  version: string;
  lifecycle: EvaluatorLifecycle;
}

export interface EvaluatorRegistryListItem {
  id: string;
  name: string;
  description: string;
  family: EvaluatorFamily;
  owner: string;
  activeVersion: EvaluatorRegistryListVersion;
  activeVersionBaseline?: EvaluatorRegistryListVersion;
  candidateVersion?: EvaluatorRegistryListVersion;
  operationalVersion: EvaluatorRegistryListVersion;
  applicationScope: string;
  environmentScope: string;
  dataBoundary: string;
  spanType?: string;
  averageLatencyMs: number;
  timeoutRate: number;
  lastTestAt: string | null;
  lastDeploymentAt: string | null;
  openIssues: number;
  version: number;
  health: {
    status: EvaluatorHealthStatus;
  };
}

export type EvaluatorResult = "pass" | "warning" | "unsafe" | "indeterminate";
export type MappingValidation = "valid" | "warning" | "unmapped";
export type DeploymentStage = "shadow" | "canary" | "production";
export type EvaluatorOutputType =
  "array" | "boolean" | "float" | "integer" | "object" | "string" | "vector";

export interface EvaluatorOutputField {
  name: string;
  type: EvaluatorOutputType;
  required: boolean;
  description?: string;
  choices?: readonly string[];
  min?: number;
  max?: number;
}

export type EvaluatorDecisionOperator = ">=" | ">" | "=" | "<=" | "<";

export interface EvaluatorDecisionRule {
  field: string;
  operator: EvaluatorDecisionOperator;
  threshold: number;
  whenMatched: EvaluatorResult;
  whenNotMatched: EvaluatorResult;
}
